<template>
  <l-map
    ref="map"
    class="map-size"
    :zoom="zoom"
    :center="center"
    :options="{
      dragging: false,
      scrollWheelZoom: false,
      touchZoom: false,
      doubleClickZoom: false,
      boxZoom: false,
      keyboard: false,
    }"
  >
    <l-tile-layer :url="url" :attribution="attribution" />
    <l-marker :lat-lng="marker" :icon="iconFlag"></l-marker>
  </l-map>
</template>

<script>
import { LMap, LTileLayer, LMarker } from "vue2-leaflet";
import L from "leaflet";
import "leaflet-overpass-layer";

// Custom marker for leaflet map
import "@fortawesome/fontawesome-free/js/all.js";
import "leaflet.awesome-markers/dist/leaflet.awesome-markers.js";
import "leaflet.awesome-markers/dist/leaflet.awesome-markers.css";

import { ref } from "@vue/composition-api";

export default {
  components: {
    LMap,
    LTileLayer,
    LMarker,
  },
  props: {
    reportData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      zoom: 17,
      url: this.$config.VUE_APP_MAP_TILE_URL,
      attribution:
        '&copy; <a href="http://osm.org/copyright"></a><a href="http://www.overpass-api.de/">Overpass API</a>',
      iconFlag: L.AwesomeMarkers.icon({
        icon: "flag",
        markerColor: "green",
        iconColor: "white",
        prefix: "fa",
      }),
      iconNursery: L.AwesomeMarkers.icon({
        icon: "baby",
        markerColor: "purple",
        iconColor: "white",
        prefix: "fa",
      }),
      iconPrimarySchool: L.AwesomeMarkers.icon({
        icon: "school",
        markerColor: "orange",
        iconColor: "white",
        prefix: "fa",
      }),
      iconSecondarySchool: L.AwesomeMarkers.icon({
        icon: "graduation-cap",
        markerColor: "blue",
        iconColor: "white",
        prefix: "fa",
      }),
      iconSuperiorSchool: L.AwesomeMarkers.icon({
        icon: "university",
        markerColor: "red",
        iconColor: "white",
        prefix: "fa",
      }),
    };
  },
  setup(props) {
    let marker = ref(
      L.latLng(
        props.reportData.center.latitude,
        props.reportData.center.longitude
      )
    );
    let center = ref(
      L.latLng(
        props.reportData.center.latitude,
        props.reportData.center.longitude
      )
    );

    return {
      marker,
      center,
    };
  },
  mounted() {
    var map = this.$refs.map.mapObject;

    var overpassOption = {
      minZoom: 17,
      timeout: 30 * 1000, // Milliseconds
      retryOnTimeout: true,
      minZoomIndicatorEnabled: false,
      endPoint: this.$config.VUE_APP_OVERPASS_URL,
      onSuccess(data) {
        for (let i = 0; i < data.elements.length; i++) {
          const e = data.elements[i];

          if (e.tags) {
            let layer;

            if (e.id in this._ids) {
              continue;
            }

            this._ids[e.id] = true;

            if (e.type === "way") {
              let found = data.elements.find((elem) => elem.id === e.nodes[0]);
              found.tags = { name: e.tags["name"] };
              this.options.saveRespVar
                ? this.options.reportData[this.options.saveRespVar].push(found)
                : null;
              let pos = L.latLng(found.lat, found.lon);
              layer = this.options.markerIcon
                ? L.marker(pos, { icon: this.options.markerIcon })
                : L.marker(pos);

              const span = document.createElement("h5");
              const div = document.createElement("div");

              span.appendChild(document.createTextNode(e.tags["name"]));

              div.appendChild(span);

              const popup = L.popup({ autoPan: false }).setContent(div);
              layer.bindPopup(popup);
            } else if (e.type === "node") {
              this.options.saveRespVar
                ? this.options.reportData[this.options.saveRespVar].push(e)
                : null;

              let pos = L.latLng(e.lat, e.lon);
              layer = this.options.markerIcon
                ? L.marker(pos, { icon: this.options.markerIcon })
                : L.marker(pos);

              const span = document.createElement("h5");
              const div = document.createElement("div");

              span.appendChild(document.createTextNode(e.tags["name"]));

              div.appendChild(span);

              const popup = L.popup({ autoPan: false }).setContent(div);
              layer.bindPopup(popup);
            } else {
              continue;
            }

            this._markers.addLayer(layer);
          }
        }
      },
    };

    var nurseries = new L.OverPassLayer(
      Object.assign({}, overpassOption, {
        reportData: this.reportData,
        saveRespVar: "nurseries",
        markerIcon: this.iconNursery,
        query:
          "(node[amenity=kindergarten]({{bbox}});way[amenity=kindergarten]({{bbox}}););out body;>;out skel qt;",
      })
    );
    map.addLayer(nurseries);

    var primarySchools = new L.OverPassLayer(
      Object.assign({}, overpassOption, {
        reportData: this.reportData,
        saveRespVar: "primarySchools",
        markerIcon: this.iconPrimarySchool,
        query:
          "(node['school:FR'='primaire']({{bbox}});way['school:FR'='primaire']({{bbox}});node['school:FR'='maternelle']({{bbox}});way['school:FR'='maternelle']({{bbox}});node['school:FR'='élémentaire']({{bbox}});way['school:FR'='élémentaire']({{bbox}}););out body;>;out skel qt;",
      })
    );
    map.addLayer(primarySchools);

    var secondarySchools = new L.OverPassLayer(
      Object.assign({}, overpassOption, {
        reportData: this.reportData,
        saveRespVar: "secondarySchools",
        markerIcon: this.iconSecondarySchool,
        query:
          "(node['school:FR'='secondaire']({{bbox}});way['school:FR'='secondaire']({{bbox}});node['school:FR'='collège']({{bbox}});way['school:FR'='collège']({{bbox}});node['school:FR'='lycée']({{bbox}});way['school:FR'='lycée']({{bbox}}););out body;>;out skel qt;",
      })
    );
    map.addLayer(secondarySchools);

    var superiorSchools = new L.OverPassLayer(
      Object.assign({}, overpassOption, {
        reportData: this.reportData,
        saveRespVar: "superiorSchools",
        markerIcon: this.iconSuperiorSchool,
        query:
          "(node['amenity'='university']({{bbox}});way['amenity'='university']({{bbox}});node['amenity'='college']({{bbox}});way['amenity'='college']({{bbox}}););out body;>;out skel qt;",
      })
    );
    map.addLayer(superiorSchools);

    setTimeout(() => {
      map.setView(this.center, 15);
    }, 600);
  },
};
</script>

<style lang="scss" scoped>
@import "~leaflet/dist/leaflet.css";

.map-size {
  height: 30rem !important;
}
</style>
